import type { FunnelStage, FunnelStageFigures, FunnelStageKey } from '@/api/types'
import { FUNNEL_STAGE_LABELS } from '@/api/types'
import { formatInteger, formatPercent } from '@/design/format'

/** No figure: a rate with no denominator, or a step that cannot be taken. Never 0. */
export const NO_FIGURE = '—'

/** A stage with no events in the period: the API's `stages_without_events`. */
export const NOT_MEASURED = 'Not measured'

/** Why a stage is not measured, in one wording wherever it is said: "No checkout started events arrived in this period". */
export function noEventsReason(stage: FunnelStageKey, period: 'this' | 'previous'): string {
  return `No ${FUNNEL_STAGE_LABELS[stage].toLowerCase()} events arrived in ${period === 'this' ? 'this' : 'the previous'} period`
}

const NOT_IN_ORDER = 'a session does not have to pass through every stage in order'

/** Under the funnel when any drop-off is negative: the API's figure, not an error. */
export const NEGATIVE_DROP_OFF_NOTE = `A negative drop-off means more sessions reached a stage than the one before it: ${NOT_IN_ORDER}.`

/** The same fact for one stage: "More sessions reached checkout started than add to basket: …". */
export function negativeDropOffReason(stage: FunnelStageKey, above: FunnelStageKey): string {
  const label = (key: FunnelStageKey) => FUNNEL_STAGE_LABELS[key].toLowerCase()
  return `More sessions reached ${label(stage)} than ${label(above)}: ${NOT_IN_ORDER}`
}

/** The id of a stage's button in the funnel table, so focus can return to it when its detail closes. */
export function stageButtonId(stage: FunnelStageKey): string {
  return `funnel-stage-${stage}`
}

/** A rate the API may leave null (no denominator): "—", never 0 %. */
export function formatRate(value: number | null): string {
  return value === null ? NO_FIGURE : formatPercent(value)
}

/** A count that may be absent: "—", never 0. */
export function formatCount(value: number | null): string {
  return value === null ? NO_FIGURE : formatInteger(value)
}

/**
 * Whether a stage had events in a period. The API lists the current period's
 * stages without events (`stages_without_events`: every stage whose sessions
 * are 0) but not the previous period's, so for that one the same rule is read
 * from the figures.
 */
export function hadEvents(figures: FunnelStageFigures): boolean {
  return figures.sessions > 0
}

/**
 * Whether a stage has a figure this period: the API's `stages_without_events`
 * (`measured: false`) does not. `measured` absent means measured.
 */
export function isMeasured(stage: FunnelStage): boolean {
  return stage.measured !== false
}

/**
 * Whether the step from `above` into `stage` is a figure this period: both
 * stages must be measured. A step, or a loss, from an unmeasured stage (or to
 * one) would compare against a count nobody measured, so it reads "—", never 0 %.
 */
export function stepComparable(stage: FunnelStage, above: FunnelStage | undefined): boolean {
  return isMeasured(stage) && above !== undefined && isMeasured(above)
}

/**
 * The previous period by the same rule: every one of `stages` was compared
 * (comparison on) and had events then. A figure reading any stage that was not
 * is "not measured then", never a change from 0 % or 100 %.
 */
export function measuredBefore(stages: readonly (FunnelStage | undefined)[]): boolean {
  return stages.every((stage) => stage?.previous != null && hadEvents(stage.previous))
}

/** The step from `above` into `stage` in the previous period: both had events then. */
export function previousStepComparable(stage: FunnelStage, above: FunnelStage | undefined): boolean {
  return above !== undefined && measuredBefore([above, stage])
}

/**
 * Whether the funnel has nothing to show: no stage had an event, or no
 * session was measured at all. That is "no measured sessions", not a funnel of zeros.
 */
export function hasNoMeasuredSessions(stages: readonly FunnelStage[]): boolean {
  const first = stages[0]
  return !first || stages.every((stage) => !isMeasured(stage)) || first.sessions === 0
}
